import clsx from 'clsx';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  Variants,
} from 'framer-motion';
import {
  forwardRef,
  useMemo,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';

import * as styles from './Modal.sty.js';
import type { ModalSizeOptionsType } from './types.js';
import { FocusScope } from '../../core/components/focus-scope/FocusScope.js';
import { OverlayContainer } from '../../core/components/OverlayContainer.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import {
  fadeInEasing,
  fadeOutEasing,
} from '../../core/utils/framer-motion-easings.js';
import { useDialog } from '../hooks/useDialog.js';
import { useOverlay } from '../hooks/useOverlay.js';

/**
 * @public
 */
export interface BaseModalProps
  extends AriaLabelingProps,
    WithChildren,
    DataTestId,
    MaskingProps,
    StylingProps {
  /**
   * Controls when the modal is shown.
   * @defaultValue false
   */
  show?: boolean;
  /**
   * Sets the size of the modal.
   * @defaultValue 'medium'
   */
  size?: ModalSizeOptionsType;
  /** Handler function that is called when the modal is closed. */
  onDismiss?: () => void;
  /**
   * Determines if you can click the backdrop to dismiss the modal.
   * If true, pressing "ESC" also dismisses the modal.
   * @defaultValue true
   */
  dismissible?: boolean;
}

// #endregion

/**
 * BaseModal component
 * @internal
 */
export const BaseModal = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  BaseModalProps & /**
   * Allows behavior tracking. This is a temporal solution and will be replaced by a hook.
   */ { 'data-dt-component'?: string }
>((props, forwardedRef) => {
  const {
    show = false,
    size = 'medium',
    children,
    onDismiss,
    dismissible = true,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    'data-dt-component': dtComponentName,
    ...remainingProps
  } = props;

  const ref = useRef(null);

  // Handle interacting outside the dialog and pressing
  // the Escape key to close the modal.
  const { overlayProps } = useOverlay(
    {
      ...props,
      onClose: () => {
        onDismiss?.();
      },
      isOpen: show,
      isKeyboardDismissDisabled: !dismissible,
    },
    ref,
  );

  // adds all aria labels to title and dialog as well as tabindex & role
  const { dialogProps, titleProps } = useDialog(remainingProps);

  const overlayContainerProps = useOverlayStacking(undefined, 'overlay');

  const reducedMotion = useReducedMotion();

  /** Animation variants */
  const animationVariants = useMemo<Record<string, Variants>>(() => {
    const duration = reducedMotion ? 0 : AnimationsJS.FadeIn.Duration.Fast;
    return {
      // When animating AnimatePresence where child components
      // will also need to transform, they can share the same enter/exit variant states.
      // The keys will need to be exactly the same, and the child will not need to
      // define `initial`, `animate` or `exit` variants on the component.
      backdrop: {
        enter: {
          // For the opacity we need to use the default value here. Framer motion would complain
          // about too complex values, when transitioning from two custom property values within
          // an rgba value apparently.
          opacity: 1,
          transition: {
            duration,
            ease: fadeInEasing,
          },
        },
        exit: {
          opacity: 0,
          transition: {
            duration,
            ease: fadeOutEasing,
          },
        },
      },
      modal: {
        enter: {
          y: 0,
          scale: 1,
          transition: {
            duration,
            ease: fadeInEasing,
          },
        },
        exit: {
          y: '5px',
          scale: 0.98,
          transition: {
            duration,
            ease: fadeInEasing,
          },
        },
      },
    };
  }, [reducedMotion]);

  return (
    <OverlayContainer
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...overlayContainerProps}
      data-dt-component={dtComponentName}
    >
      <AnimatePresence>
        {show && (
          <motion.div
            className={styles.backdrop}
            variants={animationVariants.backdrop}
            initial="exit"
            animate="enter"
            exit="exit"
            ref={forwardedRef}
            onPointerDown={(event: ReactMouseEvent) => {
              if (event.target === event.currentTarget && dismissible) {
                // The useOverlay hook keeps an internal state of the stacked overlays.
                // However, some overlays don't rely on the hook; hence the list isn't complete
                // and the overlay is dismissed when clicking on any not registered overlay.
                // (e.g. Microguide would not influence the dismissing but can't be added to
                // the list either, because it is always on top and shouldn't be closed)
                // Therefore, the sheet is manually dismissed on backdrop click.
                // This topic will be revisited and aligned with the Overlay Rework (APPDEV-3373).
                onDismiss?.();
              }
            }}
          >
            <FocusScope contain autoFocus>
              <motion.div
                // The react-aria spread needs to be cast to Record<string, any|unknown> as there
                // is a mismatch between lib.dom HTMLElement and the react-synthetic
                // event
                {...(overlayProps as Record<string, unknown>)}
                {...(dialogProps as Record<string, unknown>)}
                {...(titleProps as Record<string, unknown>)}
                className={clsx(
                  styles.base,
                  styles.sizes[size],
                  consumerClassName,
                )}
                style={consumerStyle}
                ref={ref}
                variants={animationVariants.modal}
                {...remainingProps}
              >
                <OverlayStackingProvider>{children}</OverlayStackingProvider>
              </motion.div>
            </FocusScope>
          </motion.div>
        )}
      </AnimatePresence>
    </OverlayContainer>
  );
});

(BaseModal as typeof BaseModal & { displayName: string }).displayName =
  'BaseModal';
